import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ChevronRight, User, Mail, ShieldCheck, MapPin } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { useTenant } from '../contexts/TenantContext';
import { motion } from 'motion/react';
import { useLeadership } from '../hooks/useNode';
import { useCurrentUser } from '../contexts/AuthContext';

interface LeaderRoster {
  nodeId: string;
  nodeName: string;
  level: 'LOCAL' | 'STATE' | 'NATIONAL';
  leaders: {
    userId: string;
    name: string;
    role: string;
    email: string;
    photoUrl?: string;
  }[];
}

export const LeadershipPage = () => {
  const { node, brand } = useTenant();
  const { user } = useCurrentUser();

  const { data: leadershipData, isLoading: isLeadershipLoading } = useLeadership(node?.id || '');

  const roster = useMemo(() => {
    if (!leadershipData || leadershipData.length === 0) return [];
    
    const rosterMap = new Map<string, LeaderRoster>();
    
    leadershipData.forEach(member => {
      if (!rosterMap.has(member.nodeId)) {
        rosterMap.set(member.nodeId, {
          nodeId: member.nodeId,
          nodeName: member.nodeName || 'Organization',
          level: 'LOCAL',
          leaders: []
        });
      }
      const org = rosterMap.get(member.nodeId)!;
      const currentUserId = user?.id || user?.userId || user?.clerkId;
      const isSelf = Boolean(currentUserId && (member.userId === currentUserId || member.id === currentUserId));
      const memberName = (isSelf && (user?.fullName || user?.email))
        ? (user.fullName || user.email)
        : (member.name || ((member as any).firstName || (member as any).lastName ? `${(member as any).firstName || ''} ${(member as any).lastName || ''}`.trim() : '') || member.email || member.userId || 'Administrator');
      const memberEmail = (isSelf && (user?.email || user?.primaryEmailAddress?.emailAddress))
        ? (user.email || user.primaryEmailAddress?.emailAddress)
        : member.email;
      org.leaders.push({
        userId: member.userId || member.id,
        name: memberName,
        role: member.role,
        email: memberEmail,
        photoUrl: member.avatarUrl
      });
    });
    
    return Array.from(rosterMap.values());
  }, [leadershipData, user]);

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-12">
      <header>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Leadership & Chain of Command</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          The elected officers and governing body of {node?.name} and its parent organizations.
        </p>
      </header>

      <div className="space-y-12">
        {isLeadershipLoading ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 font-mono py-8 animate-pulse">Loading leadership roster...</div>
        ) : roster.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 font-mono py-8">No leadership data available.</div>
        ) : roster.map((org, index) => (
          <section key={org.nodeId} className="relative">
            {index < roster.length - 1 && (
              <div className="absolute left-8 top-16 bottom-0 w-1 bg-gradient-to-b from-gray-100 to-transparent -mb-12" />
            )}
            
            <div className="flex items-center gap-4 mb-6 relative z-10">
              <div className={`p-2 rounded-none ${
                org.level === 'LOCAL' ? 'bg-brand-primary/10 text-brand-primary' : 'bg-navy-900 text-white'
              }`} style={{ 
                backgroundColor: org.level === 'LOCAL' ? `${brand.primary}15` : undefined,
                color: org.level === 'LOCAL' ? brand.primary : undefined
              }}>
                {org.level === 'LOCAL' ? <Shield size={24} /> : <ShieldCheck size={24} />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">{org.nodeName}</h2>
                  <Badge variant={org.level === 'LOCAL' ? 'blue' : 'navy'}>{org.level}</Badge>
                </div>
                <p className="text-xs text-gray-500">Official recognized leadership</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ml-4 md:ml-12">
              {org.leaders.map((leader) => (
                <motion.div
                  key={leader.userId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                >
                  <Card className="hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] transition-shadow group cursor-default h-full flex flex-col">
                    <div className="flex items-start gap-4 mb-4">
                      <div 
                        className="w-12 h-12 rounded-none bg-white flex items-center justify-center text-gray-400 group-hover:bg-brand-primary/5 group-hover:text-brand-primary transition-colors dark:bg-slate-800"
                        style={{ '--hover-bg': `${brand.primary}10`, '--hover-text': brand.primary } as any}
                      >
                        <User size={24} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 dark:text-gray-100 truncate">{leader.name}</p>
                        <p className="text-xs font-medium uppercase tracking-wider" style={{ color: brand.primary }}>{leader.role}</p>
                      </div>
                    </div>
                    
                    <div className="mt-auto space-y-2">
                      {leader.email ? (
                        <a 
                          href={`mailto:${leader.email}`}
                          className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 transition-colors truncate"
                          style={{ '--hover-color': brand.primary } as any}
                        >
                          <Mail size={14} className="flex-shrink-0" />
                          <span className="truncate">{leader.email}</span>
                        </a>
                      ) : (
                        <div className="flex items-center gap-2 text-xs text-gray-400 italic">
                          <Mail size={14} className="flex-shrink-0" />
                          <span>No public email</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <MapPin size={14} />
                        Verified Active Member
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </section>
        ))}
      </div>

      <Card className="bg-white border-dashed border-2 border-black dark:border-white dark:bg-slate-950">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 p-4">
          <div className="space-y-1">
            <h3 className="font-semibold text-gray-900 dark:text-white">Interested in Leadership?</h3>
            <p className="text-sm text-gray-500">Learn about our governance, review bylaws in the Digital Vault, or participate in active elections.</p>
          </div>
          <div className="flex items-center gap-3">
            <Link 
              to="/vault"
              className="text-sm font-semibold text-brand-primary hover:underline"
              style={{ color: brand.primary }}
            >
              Vault Bylaws &rarr;
            </Link>
            <div className="w-px h-6 bg-gray-200 dark:bg-gray-800" />
            <Link 
              to="/elections"
              className="text-sm font-semibold text-brand-primary hover:underline"
              style={{ color: brand.primary }}
            >
              Elections & Nominations &rarr;
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
};
